import React, { useRef, useState, useEffect } from "react";
import styles from "styles.module.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths, addWeeks } from "date-fns";
import { useTranslation } from "react-i18next";
import Filter from "./Filter";
import {
	getPurchaseStaticsApi,
	PurchaseStatisticFilter,
} from "api/purchaseStatistic";
//@ts-ignore
import "./style.css";
import { PurchaseStatisticColumnItem } from "./utils/GridColumns";
import { useUserStore } from "store";
import { UserRole } from "interface/user";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { PurchaseStatistic } from "interface/purchaseStatistic";
import PurchaseStatisticMobileList from "./PurchaseStatisticMobileList";
import { PageTypePath } from "utils/pages";

const pageSize = 60;

const PurchaseStatisticPage = () => {
	const { t } = useTranslation();
	const { roles } = useUserStore();
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [mobileData, setMobileData] = useState<PurchaseStatistic[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [gridData, setGridData] = useState<any[]>([]);

	const getDefaultFromDate = () => {
		const date = roles.includes(UserRole.Admin)
			? addWeeks(new Date(), -1)
			: addWeeks(new Date(), -1);
		date.setHours(0, 0, 0, 0);
		return date;
	};

	const getDefaultToDate = () => {
		const date = new Date();
		date.setHours(23, 59, 59, 0);
		return date;
	};

	const [filterOptions, setFilterOptions] = useState<PurchaseStatisticFilter>({
		take: pageSize,
		skip: 0,
		fromDate: getDefaultFromDate(),
		toDate: getDefaultToDate(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		const fromDate = filterOptions.fromDate instanceof Date ? filterOptions.fromDate.toISOString() : "";
		const toDate = filterOptions.toDate instanceof Date ? filterOptions.toDate.toISOString() : "";
		localStorage.setItem("fromDate", fromDate);
		localStorage.setItem("toDate", toDate);
		localStorage.setItem("buyerId", filterOptions.buyerId ?? "");
		localStorage.setItem("vesselId", filterOptions.vesselLedgerAccountNumber ?? "");

		window.open(`/${PageTypePath.Pdf}/0/4/`, "_blank");
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		setMobileLoading(true);

		getPurchaseStaticsApi({
			...filterOptions,
			skip: 0,
			take: pageSize,
		})
			.then((response) => {
				setMobileData((response || []) as PurchaseStatistic[]);
			})
			.catch((error) => {
				console.log(error);
				setMobileData([]);
			})
			.finally(() => {
				setMobileLoading(false);
			});
	}, [filterOptions, isMobileView]);

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("PurchaseStatistic")}
				</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<PurchaseStatisticMobileList
						items={mobileData}
						loading={mobileLoading}
					/>
				) : (
					<GridWithGroupPaging
						dataKey=""
						gridColumns={PurchaseStatisticColumnItem(gridData)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getPurchaseStaticsApi}
						pageable={false}
						showSelectedFilters={false}
						className={"purchaseStatistic-group-grid"}
						groupable={{ footer: "visible", enabled: false }}
						onFilteredDataChange={setGridData}
						itemAboveHeight={20}
						excelProps={{
							filterable: true,
							fileName: `${t("PurchaseStatistic")}_${new Date().toISOString()}.xlsx`,
						}}
						group={[
							{
								field: "group1",
								aggregates: [
									{ field: "netLiveWeightKg", aggregate: "sum" },
									{ field: "amount", aggregate: "sum" },
								],
							},
							{
								field: "group2",
								aggregates: [
									{ field: "netLiveWeightKg", aggregate: "sum" },
									{ field: "amount", aggregate: "sum" },
								],
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default PurchaseStatisticPage;
